import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_voyage-party/artifacts/ug2mtpsn_image.png';
const HEADSHOT_URL = 'https://customer-assets.emergentagent.com/job_voyage-party/artifacts/lr2c9gd7_ca01cc42-bf71-45be-98f9-a5b79a25bef9-1.png';

function useScrollToQuote() {
    const navigate = useNavigate();
    const location = useLocation();
    return () => {
        const go = () => document.querySelector('[data-testid="home-lead-form"]')?.scrollIntoView({ behavior: 'smooth' });
        if (location.pathname !== '/') {
            navigate('/');
            setTimeout(go, 250);
        } else {
            go();
        }
    };
}

/**
 * Rubies Global Services co-brand badge.
 * Variants:
 *  - 'inline'  : tiny horizontal mark next to the wordmark (navbar) — clickable, scrolls to quote
 *  - 'pill'    : rounded pill with Ms. Ruby + label (footer) — clickable
 *  - 'stamp'   : full bordered card with logo + "Curated by"
 *  - 'mark'    : just the round logo (icon-only)
 *  - 'stamp-light' : the light variant used on Destinations + Gallery
 */
export default function MsRubiesBadge({ variant = 'inline', className = '' }) {
    const scrollToQuote = useScrollToQuote();
    if (variant === 'inline') {
        return (
            <button
                type="button"
                data-testid="ms-rubies-badge-inline"
                onClick={scrollToQuote}
                aria-label="Book with Ms. Ruby — scroll to quote form"
                className={`inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-white/55 hover:text-white cursor-pointer transition-colors group ${className}`}
            >
                <img
                    src={HEADSHOT_URL}
                    alt="Ms. Ruby"
                    className="h-8 w-8 rounded-full object-cover ring-1 ring-[#f7022a]/70 group-hover:ring-[#f7022a] transition-all shrink-0"
                />
                <span>BY <span className="text-brand-ruby font-semibold tracking-wider">Rubies Global Services</span></span>
            </button>
        );
    }
    if (variant === 'mark') {
        return (
            <img
                data-testid="ms-rubies-badge-mark"
                src={LOGO_URL}
                alt="Rubies Global Services"
                className={`rounded-xl object-contain bg-black ring-1 ring-brand-ruby/50 shadow-[0_10px_30px_-6px_rgba(128,0,32,0.55)] ${className}`}
            />
        );
    }
    if (variant === 'pill') {
        return (
            <button
                type="button"
                data-testid="ms-rubies-badge-pill"
                onClick={scrollToQuote}
                aria-label="Book with Ms. Ruby — scroll to quote form"
                className={`relative inline-flex items-center gap-2 rounded-full overflow-hidden pl-1 pr-4 py-1 border border-white/30 shadow-[0_8px_24px_-8px_rgba(13,61,107,0.6)] hover:border-[#f7022a]/80 hover:shadow-[0_10px_28px_-8px_rgba(247,2,42,0.6)] transition-all cursor-pointer ${className}`}
            >
                <img
                    src="https://images.unsplash.com/photo-1518837695005-2083093ee35b?auto=format&fit=crop&w=900&q=85"
                    alt=""
                    className="absolute inset-0 h-full w-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-[#0D3D6B]/60 to-[#0D3D6B]/35" />
                <img
                    src={HEADSHOT_URL}
                    alt="Ms. Ruby"
                    className="relative h-9 w-9 rounded-full object-cover ring-2 ring-[#f7022a] shadow-[0_4px_12px_-2px_rgba(247,2,42,0.7)] shrink-0"
                />
                <span className="relative text-[10px] uppercase tracking-[0.25em] text-white font-bold drop-shadow-[0_1px_3px_rgba(0,0,0,0.7)]">Rubies Global Services</span>
            </button>
        );
    }
    if (variant === 'stamp-light') {
        return (
            <div
                data-testid="ms-rubies-badge-stamp-light"
                className={`relative inline-flex items-center gap-4 rounded-2xl overflow-hidden px-5 py-3 shadow-[0_18px_45px_-10px_rgba(247,2,42,0.45)] border border-white/40 ${className}`}
            >
                <img
                    src="https://images.unsplash.com/photo-1507525428034-b723cf961d3e?auto=format&fit=crop&w=1200&q=90"
                    alt=""
                    className="absolute inset-0 h-full w-full object-cover"
                />
                <div className="absolute inset-0 bg-white/10" />
                <img
                    src="https://customer-assets.emergentagent.com/job_voyage-party/artifacts/lr2c9gd7_ca01cc42-bf71-45be-98f9-a5b79a25bef9-1.png"
                    alt="Ms. Ruby"
                    className="relative h-16 w-16 rounded-full object-cover ring-2 ring-[#f7022a] shadow-[0_8px_24px_-6px_rgba(247,2,42,0.65)] shrink-0"
                />
                <div className="relative text-left">
                    <div className="text-[10px] uppercase tracking-[0.3em] font-bold drop-shadow-[0_2px_6px_rgba(255,255,255,1)]" style={{ color: '#f7022a' }}>Curated by</div>
                    <div className="font-display font-extrabold uppercase leading-none mt-0.5 tracking-tight text-base drop-shadow-[0_2px_6px_rgba(255,255,255,1)]" style={{ color: '#f7022a' }}>Rubies Global Services</div>
                </div>
            </div>
        );
    }
    // stamp
    return (
        <div
            data-testid="ms-rubies-badge-stamp"
            className={`inline-flex items-center gap-4 rounded-2xl bg-black/85 backdrop-blur border border-brand-ruby/40 px-5 py-3 shadow-[0_18px_45px_-10px_rgba(128,0,32,0.55)] ${className}`}
        >
            <img src={LOGO_URL} alt="Rubies Global Services" className="h-14 w-14 rounded-lg object-contain bg-black ring-1 ring-brand-ruby/40" />
            <div className="text-left">
                <div className="text-[10px] uppercase tracking-[0.3em] text-white/55">Curated by</div>
                <div className="font-display font-extrabold uppercase text-brand-ruby leading-none mt-0.5 tracking-tight text-base">Rubies Global Services</div>
            </div>
        </div>
    );
}

export { LOGO_URL as RUBIES_LOGO };
